// Components==============
import {
  faCalendar,
  faFlag,
  faPlay,
  faStop,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AnimatePresence, AnimateSharedLayout, motion } from 'framer-motion';
import { useOnClickOutside } from 'hooks/useOnClickOutside';
import { useRedux } from 'hooks/useRedux';
import { ProjectDetailsContext } from 'pages/Workspace/ProjectDetails/ProjectDetails';
import React, { useContext, useEffect, useRef, useState } from 'react';
import DayPicker from 'react-day-picker';
import { framerFade, interactiveScale } from 'utilities/FramerAnimations';
import { getDateString } from 'utilities/getDateString';
import { ProcessContext } from '../ProjectProcess/components/Process/Process';
import './Calender.module.scss';
// =========================

function getCorrectIcon(option: DateType) {
  if (option === 'calender') return faCalendar;
  if (option === 'play') return faPlay;
  if (option === 'stop') return faStop;
  return faFlag;
}

export default function Calender() {
  const { handleProcessSave, processGroups, project, isLocked } = useContext(
    ProjectDetailsContext
  );
  const { process, phase } = useContext(ProcessContext);

  const [dateIsVisible, setDateIsVisible] = useState(false);
  const [CalenderIsOpen, setIsOpen] = useState(false);

  const options: DateType[] = ['calender', 'play', 'stop', 'flag'];

  const [counter, setCounter] = useState(0);
  const [activeOption, setActiveOption] = useState<DateType>(
    process?.date_type || 'calender'
  );
  const [date, setDate] = useState(
    process?.date ? new Date(process?.date) : undefined
  );

  const userId = useRedux((state) => state.authentication?.user?.id);
  const isTeacher =
    project?.members.find((m) => m.user_id === userId)?.membership ===
    'teacher';

  const dateOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };

  const ref = useRef<HTMLDivElement>(null);
  const calenderRef = useRef<HTMLDivElement>(null);

  const { isOpen } = useContext(ProjectDetailsContext);

  useOnClickOutside({
    refs: [ref, calenderRef],
    handler: () => {
      setIsOpen(false);
      setDateIsVisible(false);
    },
    condition: CalenderIsOpen,
  });

  useEffect(() => {
    if (process?.date && !date) setDate(new Date(process.date));
  }, [process?.date]);

  useEffect(() => {
    setCounter(1);
    if (counter === 0) return;

    const newValue = processGroups.map((group) =>
      group.id === phase
        ? {
            ...group,
            activities: group.activities.map((activity) =>
              activity.processId === process?.id
                ? {
                    ...activity,
                    date: date
                      ? `${date.getFullYear()}-${
                          date.getMonth() + 1
                        }-${date.getDate()}`
                      : undefined,
                    date_type: activeOption,
                  }
                : activity
            ),
          }
        : group
    );

    handleProcessSave(newValue);
  }, [activeOption, date?.getTime()]);

  const conditionalScale = isTeacher ? {} : interactiveScale;

  return (
    <div styleName="wrapper">
      <AnimateSharedLayout>
        <motion.div
          styleName={`circle ${!date ? 'inactive' : ''}`}
          onHoverStart={() => process?.date && setDateIsVisible(true)}
          onHoverEnd={() => {
            if (CalenderIsOpen) return;
            setDateIsVisible(false);
          }}
          {...conditionalScale}
          layout
          key={isOpen ? 1 : 0}
          initial={{ borderRadius: 20 }}
          onClick={
            isTeacher || isLocked ? undefined : () => setIsOpen((prev) => !prev)
          }
          style={{ cursor: isTeacher ? 'default' : 'pointer' }}
          ref={ref}
        >
          <AnimatePresence>
            {dateIsVisible && (
              <motion.p {...framerFade}>
                {/* @ts-ignore */}
                {date?.toLocaleDateString('nl-NL', dateOptions)}
              </motion.p>
            )}
            <motion.div layoutId="icon" styleName="icon">
              <FontAwesomeIcon icon={getCorrectIcon(activeOption)} />
            </motion.div>
          </AnimatePresence>
        </motion.div>
      </AnimateSharedLayout>
      <AnimatePresence>
        {CalenderIsOpen && (
          <motion.div {...framerFade} styleName="calender" ref={calenderRef}>
            <div styleName="options">
              {options.map((option) => (
                <div
                  styleName={`option ${
                    activeOption === option ? 'active' : ''
                  }`}
                  key={option}
                  onClick={() => setActiveOption(option)}
                >
                  <FontAwesomeIcon icon={getCorrectIcon(option)} />
                </div>
              ))}
            </div>
            <DayPicker
              onDayClick={(d) => {
                if (date && getDateString(d) === getDateString(date)) {
                  setDateIsVisible(false);
                  setTimeout(() => {
                    setDate(undefined);
                  }, 400);
                  return;
                }

                setDateIsVisible(true);
                setDate(d);
              }}
              firstDayOfWeek={1}
              weekdaysShort={['Z', 'M', 'D', 'W', 'D', 'V', 'Z']}
              selectedDays={date}
              initialMonth={date}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
